import React, {Dispatch, FC, KeyboardEventHandler, SetStateAction, useEffect, useMemo, useState} from "react";
import {Button} from "../../../../lib/src/components/form/Button";
import {faCalendar} from "@fortawesome/free-regular-svg-icons";
import {ProjectVisit} from "../../../../lib/src/api/dto/ProjectVisit";
import {useNavigate} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faChevronRight, faQuestion, faSquareCheck} from "@fortawesome/free-solid-svg-icons";
import {ProjectVisitItemCard} from "./ProjectVisitSearchItemCard";
import {useModal} from "@traas/lib/src/components/layout/ModalProvider";
import {AddProjectVisitModal} from "../../modals/AddProjectVisitModal";
import {useApi} from "../../api/APIContext";
import {ZipcodeZone} from "@traas/lib/src/api/dto/ZipcodeZone";
import {faSquare} from "@fortawesome/pro-light-svg-icons";
import {faExternalLink} from "@fortawesome/pro-solid-svg-icons";
import config from "../../config";
import {ProjectVisitItem} from "./type";
import {getToken, usePersistentState} from "@ventumsoftware/react-toolkit";
import {projectVisitStatusEnum, projectVisitTypeEnum} from "@traas/lib/src/api/dto/ProjectVisit";
import {Input} from "@traas/lib/src/components/form/Input";
import {Select} from "@traas/lib/src/components/form/Select";
import {Project} from "@traas/lib/src/api/dto/Project";
import {MapWithProjectVisitMarkers, ProjectVisitMapMarker} from "@traas/lib/src/components/content/MapWithEmployeeMarkers";
import {useApiService} from "../../../../lib/src/api/hooks/useApiService";
import {ProjectVisitMapConfig} from "@traas/lib/src/api/dto/ProjectVisitMapConfig";
import moment from "moment";

interface ProjectVisitSearchProps {
  projectVisits: ProjectVisit[]
}

export const ProjectVisitSearch: FC<ProjectVisitSearchProps> = (props) => {
  const navigate = useNavigate()
  const { projects, zipcodeZones, projectVisitMapConfigurations } = useApi()
  const [search, setSearch] = useState('')
  const [cursor, setCursor] = useState<number>(-1)

  const [selectedZipcodeZones, setSelectedZipcodeZones] = usePersistentState<string[]>('project-visit-filters-zipcode',[])
  const [selectedStatuses, setSelectedStatuses] = usePersistentState<string[]>('project-visit-filters-status',[])
  const [selectedTypes, setSelectedTypes] = usePersistentState<string[]>('project-visit-filters-type',[])

  const [from, setFrom] = usePersistentState<Date>('project-visit-filters-from', new Date(moment().startOf('month').toDate()))
  const [to, setTo] = usePersistentState<Date>('project-visit-filters-to', new Date(moment().endOf('month').toDate()))
  const [sortDirection, setSortDirection] = usePersistentState<string>('project-visit-filters-sort-direction', 'desc')

  const items = useMemo(() => {
    return [
      ...props.projectVisits.map((visit) => {
        let project = projects.find((p) => p.id === visit.projectId)

        const relatedZipcodeZones = project?.deliveryZipCode ? (() => {
            const zipNumber = parseInt(
              project.deliveryZipCode.slice(0, 4),
            );

            if (isNaN(zipNumber)) return [];

            return zipcodeZones
              .filter((zz) => zipNumber >= zz.zipcodesFrom && zipNumber <= zz.zipcodesTo)
              .map((rzz) => rzz.id);
          })() : [];

        return {
          id: visit.id,
          projectId: visit.projectId,
          taskId: visit.taskId,
          name: visit.name,
          status: visit.status,
          type: visit.type,
          targetDate: visit.targetDate,
          createdAt: visit.createdAt,
          updatedAt: visit.UpdatedAt,
          navigate: () => navigate(`/werkbezoeken/${visit.id}`),
          ...(project != null ? {project: project} : {}),
          relatedZipcodeZones,
          visit: visit
        }
      })
    ].sort((a, b) => a.createdAt.getTime() < b.createdAt.getTime() ? 1 : -1);
  }, [props.projectVisits])

  const filteredItems = useMemo(() => {
    const searchValue = search?.toLowerCase() ?? "";

    return items.filter((item) => {
      const name = item?.name?.toLowerCase() ?? "";
      const zip = item?.project?.deliveryZipCode?.toLowerCase() ?? "";
      const address = item?.project?.deliveryAddressLine.toLowerCase() ?? "";
      const city = item?.project?.deliveryCity?.toLowerCase() ?? "";
      const postalCode = item?.project?.deliveryZipCode?.toLowerCase() ?? "";
      const relatedZipcodeZones = item.relatedZipcodeZones
      const type = item?.type ?? "";
      const status = item?.status ?? "";
      const projectNames = item?.project?.description.toLowerCase() ?? "";
      const projectNumbers = item?.project?.projectNumber.toLowerCase() ?? "";

      const matchWindow = moment(item.targetDate).isBetween(from, to);

      const matchesSearch =
        name.includes(searchValue) ||
        zip.includes(searchValue) ||
        address.includes(searchValue) ||
        city.includes(searchValue) ||
        postalCode.includes(searchValue) ||
        projectNames.includes(searchValue) ||
        projectNumbers.includes(searchValue)
      ;

      const matchesZone =
        selectedZipcodeZones.length === 0 ||
        selectedZipcodeZones.some((zoneId) =>
          relatedZipcodeZones.includes(zoneId)
        );

      const matchesType =
        selectedTypes.length === 0 ||
        selectedTypes.includes(type)

      const matchesStatus =
        selectedStatuses.length === 0 ||
        selectedStatuses.includes(status)


      return matchWindow && matchesSearch && matchesZone && matchesType && matchesStatus;
    });

  }, [items, search, selectedZipcodeZones, selectedTypes, selectedStatuses, from, to]);
  filteredItems.sort((a, b) =>
    sortDirection === 'desc'
      ? a.targetDate < b.targetDate ? 1 : -1
      : a.targetDate > b.targetDate ? 1 : -1
  )

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'ArrowUp') {
      setCursor(old => Math.max(0, old - 1))
    }
    if (event.key === 'ArrowDown') {
      setCursor(old => Math.min(filteredItems.length - 1, old + 1))
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const item = filteredItems[cursor]
      if (! item) {
        console.warn(`Cannot find item on cursor position ${cursor}`, item, filteredItems)
      } else {
        item.navigate()
      }
    }
  }

  useEffect(() => {
    if (cursor > filteredItems.length - 1) {
      setCursor(filteredItems.length - 1)
    }
  }, [cursor, filteredItems]);

  const allSelectedItems = [...selectedTypes, ...selectedStatuses, ...selectedZipcodeZones];

  // MAP states
  const [isMapCollapsed, setIsMapCollapsed] = usePersistentState<boolean>('map-collapsed', true)

  return <div className={"flex h-[calc(100vh-4rem)] w-full"}>
      <div className={"flex-1 overflow-auto"}>
        <div className={"my-8 px-4 max-w-(--breakpoint-lg) mx-auto"}>
          <ProjectVisitMainContent
            projectVisits={props.projectVisits}
            allSelectedItems={allSelectedItems}
            items={items}
            filteredItems={filteredItems}
            selectedStatuses={selectedStatuses} setSelectedStatuses={setSelectedStatuses}
            selectedTypes={selectedTypes} setSelectedTypes={setSelectedTypes}
            zipcodeZones={zipcodeZones} selectedZipcodeZones={selectedZipcodeZones} setSelectedZipcodeZones={setSelectedZipcodeZones}
            search={search} setSearch={setSearch}
            cursor={cursor} setCursor={setCursor}
            handleKeyDown={handleKeyDown}
            from={from} setFrom={setFrom}
            to={to} setTo={setTo}
            sortDirection={sortDirection} setSortDirection={setSortDirection}
          />
      </div>
    </div>

    <ProjectVisitMapContent
      isMapCollapsed={isMapCollapsed} setIsMapCollapsed={setIsMapCollapsed}
      projectVisits={props.projectVisits}
      filteredItems={filteredItems}
      mapConfigurations={projectVisitMapConfigurations}
    />
  </div>
}

const ProjectVisitMapContent: FC<{
  isMapCollapsed: boolean, setIsMapCollapsed: Dispatch<SetStateAction<boolean>>,
  projectVisits: ProjectVisit[],
  filteredItems: {
    relatedZipcodeZones: string[]
    visit: ProjectVisit
    project?: Project | undefined
    id: string
    projectId: string
    taskId: string
    name: string
    status: "Concept" | "Taak aangemaakt" | "Ingepland" | "Uitgevoerd"
    type: "Voor uitvoering" | "Na uitvoering" | "Werkopname"
    targetDate: Date
    createdAt: Date
    updatedAt: Date
    navigate: () => (void | Promise<void>)
  }[],
  mapConfigurations: ProjectVisitMapConfig[]
}> = (props) => {
  const navigate = useNavigate()
  const {projectService} = useApiService(config, getToken, navigate)

  type Marker = {
    id: string;
    lat: number;
    lng: number;
  };

  const [markers, setMarkers] = useState<Marker[]>([]);

  useEffect(() => {
    const itemsWithAddress = props.filteredItems.filter(
      item => item.project?.deliveryAddressStreetname != null
    );

    Promise.all(itemsWithAddress.map(async item => {
        const coordinates = await projectService.getCoordinates(item.projectId);
        return {
          id: item.projectId,
          lat: coordinates.lat,
          lng: coordinates.lng,
        };
      })
    ).then(setMarkers);
  }, [props.filteredItems]);


  return <div className={`transition-all bg-white border-l shadow-lg rounded-lg duration-300 relative ${props.isMapCollapsed ? "w-12" : "hidden lg:block lg:w-1/3 xl:w-1/2 2xl:w-1/3"}`}>
      <div onClick={() => props.setIsMapCollapsed(prev => !prev)} className={"absolute left-0 top-[calc(50%-50px)] -translate-x-full bg-white border rounded-l px-0.5 py-10 hover:cursor-pointer hover:bg-brand-50"}>
        <FontAwesomeIcon icon={props.isMapCollapsed ? faChevronLeft : faChevronRight}/>
      </div>

      <div className={"h-full w-full"}>
        <div className={"h-full w-full"}>
          <MapWithProjectVisitMarkers center={{ lat: 51.4808111, lng: 3.8153836 }} zoom={10} markers={
            markers.map((marker, index): ProjectVisitMapMarker | null => {
                const projectVisit = props.projectVisits[index];

                if (!projectVisit) {
                  return null;
                }

                const diffInDays = moment(projectVisit.targetDate).diff(moment(), "days");
                const mapConfig = props.mapConfigurations
                  .filter(mc => mc.withinNDays >= diffInDays)
                  .sort((a, b) => a.withinNDays - b.withinNDays)[0];

                if (!mapConfig) {
                  return null;
                }

                return {pos: {lat: marker.lat, lng: marker.lng,}, projectVisit, color: mapConfig.color,};
              }).filter((m): m is ProjectVisitMapMarker => m !== null)
            }
          />
        </div>
      </div>
    </div>
}

const ProjectVisitMainContent: FC<{
  projectVisits: ProjectVisit[],
  allSelectedItems: string[],
  items: {
    relatedZipcodeZones: string[]
    visit: ProjectVisit
    project?: Project | undefined
    id: string
    projectId: string
    taskId: string
    name: string
    status: "Concept" | "Taak aangemaakt" | "Ingepland" | "Uitgevoerd"
    type: "Voor uitvoering" | "Na uitvoering" | "Werkopname"
    targetDate: Date
    createdAt: Date
    updatedAt: Date
    navigate: () => (void | Promise<void>)
  }[],
  filteredItems: {
    relatedZipcodeZones: string[]
    visit: ProjectVisit
    project?: Project | undefined
    id: string
    projectId: string
    taskId: string
    name: string
    status: "Concept" | "Taak aangemaakt" | "Ingepland" | "Uitgevoerd"
    type: "Voor uitvoering" | "Na uitvoering" | "Werkopname"
    targetDate: Date
    createdAt: Date
    updatedAt: Date
    navigate: () => (void | Promise<void>)
  }[],
  selectedStatuses: string[], setSelectedStatuses: Dispatch<SetStateAction<string[]>>,
  selectedTypes: string[], setSelectedTypes: Dispatch<SetStateAction<string[]>>,
  zipcodeZones: ZipcodeZone[], selectedZipcodeZones: string[], setSelectedZipcodeZones: Dispatch<SetStateAction<string[]>>,
  search: string, setSearch: Dispatch<SetStateAction<string>>,
  cursor: number, setCursor: Dispatch<SetStateAction<number>>,
  handleKeyDown: KeyboardEventHandler<HTMLInputElement>,
  from: Date, setFrom: Dispatch<SetStateAction<Date>>,
  to: Date, setTo: Dispatch<SetStateAction<Date>>,
  sortDirection: string, setSortDirection: Dispatch<SetStateAction<string>>
}> = (props) => {
  const {crmUrl} = config
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)
  const addProjectVisitModal = useModal({title: 'Werkbezoek aanmaken', body: <AddProjectVisitModal shouldMakeTask={false} />, size: 'lg'})

  return <>
    {/* Header */}
    <div className={'flex justify-between items-center h-12 mb-4'}>
      <h1 className={'flex items-center text-2xl font-bold'}>Werkbezoeken</h1>
      <Button type={'primary'} size={'md'} text={'Werkbezoek aanmaken'} icon={faCalendar} onClick={() => addProjectVisitModal.open()} />
    </div>

    <div className={''}>
      <div className={"flex flex-row gap-4 items-center mb-4"}>
        <input className={"border-2 border-slate-200 outline-brand-700 text-black rounded-sm text-base font-normal px-3 py-2 w-full"} type={"search"} value={props.search} placeholder={"Zoeken..."} onChange={e => props.setSearch(e.target.value)} onKeyDown={props.handleKeyDown} autoFocus/>

        <div className={"relative"}>
          {props.allSelectedItems.length > 0 && <div className={"absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center border-2 border-white"}>
            {props.allSelectedItems.length}
          </div>}
          <div className={"py-2 px-3 border-2 border-slate-200 rounded-lg bg-brand-900 text-white hover:cursor-pointer hover:bg-brand-800"} onClick={() => {setIsCollapsed(prev => !prev)}}>
            <div>
              Filters
            </div>
          </div>

          {!isCollapsed && <div className={"fixed inset-0 z-10 bg-[#0001] dark:bg-[#0002]"} onClick={() => setIsCollapsed(true)}/>}
          {!isCollapsed && <div className={"absolute z-20 min-w-[calc(100%+10px)] border border-slate-200 bg-zinc-50 rounded-sm right-0 top-12 shadow-lg -mx-px px-3"}
                                onClick={(e) => e.stopPropagation()}>
            <div className={"flex flex-1 items-center justify-between w-80 -mx-3 px-3 py-2 pb-2 text-sm font-medium text-brand-900 border-b border-slate-200"}>
              <div>Filteren op status</div>
            </div>
            {Object.entries(projectVisitStatusEnum).map(([key, value], i) => {
              return <FilterStatusRow key={i} item={value} projectVisits={props.items} selected={props.selectedStatuses.includes(value)} setSelected={(selected) => {
                if (selected) {
                  props.setSelectedStatuses(old => [...old, value])
                } else {
                  props.setSelectedStatuses(old => old.filter(status => status !== value))
                }
              }}/>
            })}

            <div className={"flex flex-1 items-center justify-between w-80 -mx-3 px-3 py-2 pb-2 text-sm font-medium text-brand-900 border-b border-slate-200"}>
              <div>Filteren op type</div>
            </div>
            {Object.entries(projectVisitTypeEnum).map(([key, value], i) => {
              return <FilterTypeRow key={i} item={value} projectVisits={props.items} selected={props.selectedTypes.includes(value)} setSelected={(selected) => {
                if (selected) {
                  props.setSelectedTypes(old => [...old, value])
                } else {
                  props.setSelectedTypes(old => old.filter(type => type !== value))
                }
              }}  />
            })}

            <div className={"flex flex-1 items-center justify-between w-80 -mx-3 px-3 py-2 pb-2 text-sm font-medium text-brand-900 border-b border-slate-200"}>
              <div>Filteren op postcodes</div>
              <div className={'px-1.5 py-1 hover:bg-brand-100 rounded-sm hover:cursor-pointer'} onClick={() => {window.location.href = `${crmUrl}/settings/zipcode-zones`}}>
                <FontAwesomeIcon icon={faExternalLink} />
              </div>
            </div>
            {props.zipcodeZones.sort((a, b) => a.zipcodesFrom > b.zipcodesFrom ? 1 : -1)
              .map((zz, i, arr) => {
                const isFirst = i === 0
                const isLast = i === arr.length - 1
                return <ZipcodeZoneRow key={i} zipcodeZone={zz} projectVisits={props.items} selected={props.selectedZipcodeZones.includes(zz.id)} isFirst={isFirst} isLast={isLast}
                                       setSelected={(selected) => {
                                         if (selected) {
                                           props.setSelectedZipcodeZones(old => [...old, zz.id])
                                         } else {
                                           props.setSelectedZipcodeZones(old => old.filter(id => id !== zz.id))
                                         }
                                       }}/>
              })}
          </div>}
        </div>
      </div>

      <div className={'flex flex-row'}>
        <div className={'flex flex-1 items-center mb-4 space-x-4'}>
        <Input inline={true} required={false} type={'date'} label={'Van:'} value={props.from} onChange={props.setFrom} />
        <Input inline={true} required={false} type={'date'} label={'Tot:'} value={props.to} onChange={props.setTo} />
        <div className={"px-2 py-1.5 mt-1 text-brand-900 hover:text-brand-700 hover:cursor-pointer hover:underline hover:bg-brand-50 rounded-sm"} onClick={() => {
            props.setFrom(moment().startOf('isoWeek').toDate())
            props.setTo(moment().endOf('isoWeek').toDate())
          }}>Deze week</div>
        <div className={"px-2 py-1.5 mt-1 text-brand-900 hover:text-brand-700 hover:cursor-pointer hover:underline hover:bg-brand-50 rounded-sm"} onClick={() => {
            props.setFrom(moment().startOf('month').toDate())
            props.setTo(moment().endOf('month').toDate())
          }}>Deze maand</div>
        <div className={"px-2 py-1.5 mt-1 text-brand-900 hover:text-brand-700 hover:cursor-pointer hover:underline hover:bg-brand-50 rounded-sm"} onClick={() => {
            props.setFrom(moment().startOf('year').toDate())
            props.setTo(moment().endOf('year').toDate())
          }}>Dit jaar</div>
        </div>
        <div>
          <Select label={''} options={{desc: 'Nieuwste', asc: 'Oudste'}} value={props.sortDirection} onChange={props.setSortDirection} />
        </div>
      </div>

      {props.projectVisits.length === 0 && <div className={'flex flex-col bg-slate-100 border-2 border-slate-200 h-64 rounded-sm items-center justify-center'}>
        <FontAwesomeIcon icon={faQuestion} className={'text-3xl'} />
        <h1 className={'pb-2'}>Er zijn nog geen werkbezoeken</h1>
        <Button type={'primary'} size={'md'} text={'Werkbezoek inplannen'} icon={faCalendar} onClick={() => addProjectVisitModal.open()} />
      </div>}

      {...props.filteredItems.map((item, i, arr) => {
        const isFirst = i === 0
        const isLast = i === arr.length - 1
        const isSelected = props.cursor === i
        return <div key={i} className={`${isFirst ? 'rounded-t-lg border' : 'border-b border-l border-r'} ${isLast && 'rounded-b-lg'} border-slate-200 ${isSelected ? 'bg-brand-100' : 'bg-white'}`}>
          <ProjectVisitItemCard item={item} key={i}/>
        </div>;
      })}
    </div>
  </>
}

const ZipcodeZoneRow: FC<{ zipcodeZone: ZipcodeZone, projectVisits: ProjectVisitItem[], selected?: boolean, setSelected?: (selected: boolean) => void, isFirst?: boolean, isLast?: boolean }> = (props) => {
  const handleClick = () => {
    console.log("clock")

    props.setSelected?.(!props.selected)
  }

  const visitCount = props.projectVisits.filter((visit) =>
    visit.relatedZipcodeZones?.includes(props.zipcodeZone.id)
  ).length;

  return <div className={`flex flex-row gap-4 items-center px-3 py-3 -mx-3 hover:cursor-pointer hover:bg-brand-50 ${props.isLast ? "" : "border-b border-slate-200"}`} onClick={(e) => {
    e.stopPropagation()
    handleClick()
  }}>
      <div className={"hover:bg-brand-100 px-1.5 py-1 rounded-sm text-brand-900"} onClick={(e) => {
        e.stopPropagation()
        handleClick()
      }}>
        <FontAwesomeIcon icon={props.selected ? faSquareCheck : faSquare}/>
      </div>

      <div className={"flex-1 flex flex-col"}>
        <div>{props.zipcodeZone.name}</div>
        <div className={"text-xs text-slate-800"}>
          ({props.zipcodeZone.zipcodesFrom} -{" "}
          {props.zipcodeZone.zipcodesTo})
        </div>
      </div>

      <div className={"text-xs text-slate-800"}>
        {visitCount} bezoeken
      </div>
    </div>
}

const FilterStatusRow: FC<{ item: string, projectVisits: ProjectVisitItem[], selected?: boolean, setSelected?: (selected: boolean) => void, isLast?: boolean, isFirst?: boolean }> = (props) => {
  const handleClick = () => {
    props.setSelected?.(!props.selected)
  }

  const itemCount = props.projectVisits.filter((visit) =>
    visit.status.includes(props.item)
  ).length;

  return <div className={`flex flex-row gap-4 items-center px-3 py-2 -mx-3 hover:cursor-pointer hover:bg-brand-50 ${props.isLast ? "" : "border-b border-slate-200"}`} onClick={(e) => {
    e.stopPropagation()
    handleClick()
  }}>
    <div className={"hover:bg-brand-100 px-1.5 py-1 rounded-sm text-brand-900"} onClick={(e) => {
      e.stopPropagation()
      handleClick()
    }}>
      <FontAwesomeIcon icon={props.selected ? faSquareCheck : faSquare}/>
    </div>

    <div className={'flex-1'}>{props.item}</div>

    <div className={"text-xs text-slate-800"}>
      {itemCount} bezoeken
    </div>
  </div>
}

const FilterTypeRow: FC<{ item: string, projectVisits: ProjectVisitItem[], selected?: boolean, setSelected?: (selected: boolean) => void, isLast?: boolean, isFirst?: boolean }> = (props) => {
  const handleClick = () => {
    props.setSelected?.(!props.selected)
  }

  const itemCount = props.projectVisits.filter((visit) =>
    visit.type.includes(props.item)
  ).length;

  return <div className={`flex flex-row gap-4 items-center px-3 py-2 -mx-3 hover:cursor-pointer hover:bg-brand-50 ${props.isLast ? "" : "border-b border-slate-200"}`} onClick={(e) => {
    e.stopPropagation()
    handleClick()
  }}>
    <div className={"hover:bg-brand-100 px-1.5 py-1 rounded-sm text-brand-900"} onClick={(e) => {
      e.stopPropagation()
      handleClick()
    }}>
      <FontAwesomeIcon icon={props.selected ? faSquareCheck : faSquare}/>
    </div>

    <div className={'flex-1'}>{props.item}</div>

    <div className={"text-xs text-slate-800"}>
      {itemCount} bezoeken
    </div>
  </div>
}
